import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Grid2 as Grid,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useDispatch, useSelector } from "react-redux";
import {
  setCommonValue,
  updateScheduleOfValuesNeedCreate,
} from "../../../store/actions/uiActions/myPermitsActions";

const RenderField = ({
  field,
  values,
  handleChange,
  setFieldValue,
  errors,
  touched,
  selectedDocuments,
  disableJobCost = false,
}) => {
  const value = values[field.name] ?? "";
  const isEmptyRequired =
    field.required && (value === "" || value === null || value === undefined);

  const dispatch = useDispatch();

  // Skip validation for job_cost field when Schedule of Values is selected
  const shouldSkipValidation = field.name === "job_cost" && disableJobCost;
  const isError = shouldSkipValidation
    ? false
    : !!(touched[field.name] && errors[field.name]);
  const hasRedBorder = shouldSkipValidation
    ? false
    : isError || isEmptyRequired;

  const borderStyle = hasRedBorder ? "1px solid #f44336" : undefined;

  const commonProps = {
    fullWidth: true,
    name: field.name,
    label: field.label + (field.required ? " *" : ""),
    value: value,
    onChange: (e) => {
      dispatch(setCommonValue(field?.name, value));
      handleChange(e);
      if (field.child && field.child.options) {
        const childFieldName = field.child.name;
        const newParentValue = e.target.value;
        const validChildOptions = field.child.options?.[newParentValue] || [];
        const validValues = validChildOptions.map((opt) => opt.value);
        if (!validValues.includes(values[childFieldName])) {
          setFieldValue(childFieldName, "");
        }
      }
    },
    error: isError,
    helperText: isError ? errors[field.name] : "",
    multiline: field.name === "work_description",
    minRows: field.name === "work_description" ? 3 : undefined,
    sx: {
      border: borderStyle,
      borderRadius: hasRedBorder ? "10px" : undefined,
      "& .MuiInputLabel-root": {
        color: hasRedBorder ? "#f44336" : undefined,
      },
      "& .MuiOutlinedInput-root": {
        "&.Mui-error": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: shouldSkipValidation
              ? "rgba(0, 0, 0, 0.23)"
              : undefined,
          },
        },
      },
      width: field.name === "work_description" ? "100%" : undefined,
    },
  };

  if (["text", "int", "float"].includes(field.type)) {
    const isJobCostField = field.name === "job_cost";
    const isDisabled = isJobCostField ? disableJobCost : false;

    return (
      <TextField
        {...commonProps}
        disabled={isDisabled}
        type={field.type === "float" ? "number" : "text"}
      />
    );
  }

  if (field.type === "bool") {
    let hasNTBO = false;
    let isPPForm = false;

    if (field.name === "intends_to_use_a_private_provider_for_plan_review") {
      hasNTBO = selectedDocuments.some((doc) => doc.name === "pp_ntbo");
      isPPForm = true;
    }
    return (
      <FormControl
        component="fieldset"
        error={isError}
        sx={{ border: borderStyle, borderRadius: 1, p: 0 }}
      >
        <FormLabel component="legend">
          {field.label + (field.required ? " *" : "")}
        </FormLabel>
        <RadioGroup
          row
          name={field.name}
          value={String(value)}
          onChange={handleChange}
        >
          {(field.options || []).map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={String(opt.value)}
              control={<Radio disabled={isPPForm && hasNTBO} />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }

  if (field.type === "select") {
    return (
      <TextField
        {...commonProps}
        select
        value={String(value) || ""}
        slotProps={{
          select: {
            MenuProps: {
              PaperProps: {
                style: {
                  marginTop: "1.5px",
                  maxHeight: 220,
                  width: "auto",
                  maxWidth: "100%",
                },
              },
            },
          },
        }}
      >
        {(field.options || []).map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Box
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "230px",
              }}
            >
              {opt.label}
            </Box>
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return null;
};

const DelayedChildRender = ({ shouldRender, children }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldRender) {
      const timeout = setTimeout(() => setVisible(true), 0);
      return () => clearTimeout(timeout);
    } else {
      setVisible(false);
    }
  }, [shouldRender]);

  return visible ? children : null;
};

const JaxFormFields = ({
  formik: { values, handleChange, setFieldValue, errors, touched },
  grouped,
  expanded,
  setExpanded,
  setCategory,
  disableJobCost,
  setDisableJobCost,
  setUserHasSelected,
}) => {
  const { jaxFields, jaxFieldsLoading } = useSelector(
    (state) => state.myPermitsComponent.citySite
  );
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);

  const dispatch = useDispatch();

  if (jaxFieldsLoading || !jaxFields) return null;
  if (!grouped || Object.keys(grouped).length === 0) return null;

  return (
    <>
      {Object.entries(grouped || {}).map(([category, fields]) => {
        const totalRequired = fields.filter((f) => f.required).length;
        const totalRequiredFilled = fields.filter(
          (f) =>
            f.required &&
            values[f.name] !== null &&
            values[f.name] !== undefined &&
            values[f.name] !== ""
        ).length;

        const totalFilled = fields.filter(
          (f) =>
            values[f.name] !== null &&
            values[f.name] !== undefined &&
            values[f.name] !== ""
        ).length;
        const total = fields.length;

        return (
          <Accordion
            key={category}
            expanded={expanded === category}
            onChange={() =>
              setExpanded(expanded === category ? false : category)
            }
            slotProps={{
              transition: { unmountOnExit: true, timeout: "auto" },
            }}
            sx={{
              mb: 2,
              boxShadow: "2px 3px 4px 1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color:
                      totalRequiredFilled < totalRequired ? "red" : "green",
                  }}
                />
              }
            >
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ flexGrow: 1 }}
              >
                {category.replace(/_/g, " ").toUpperCase()}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: totalRequiredFilled < totalRequired ? "red" : "green",
                }}
              >
                {totalFilled}/{total}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {category === "general" && (
                  <Grid size={12}>
                    <Box
                      sx={{
                        p: 2,
                        mb: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="primary"
                        sx={{ mb: 1 }}
                      >
                        Use Our Schedule of Values Instead of Uploading a
                        Contract?
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <FormControl component="fieldset">
                          <RadioGroup
                            row
                            value={String(disableJobCost)}
                            onChange={(e) => {
                              const newValue = e.target.value === "true";

                              setUserHasSelected(true);
                              dispatch(
                                updateScheduleOfValuesNeedCreate(newValue)
                              );
                            }}
                          >
                            {[
                              { val: "true", label: "Yes" },
                              { val: "false", label: "No" },
                            ].map((opt) => (
                              <FormControlLabel
                                key={opt.label}
                                value={opt.val}
                                control={<Radio />}
                                label={opt.label}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                        {disableJobCost && (
                          <Button
                            onClick={() => setCategory("schedule")}
                            size="small"
                            endIcon={<LaunchIcon />}
                            variant="outlined"
                          >
                            Manage
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}
                {fields.map((field, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      size={{
                        xs: 12,
                        md: field.name === "work_description" ? 8 : 4,
                      }}
                    >
                      <RenderField
                        field={field}
                        values={values}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        touched={touched}
                        selectedDocuments={selectedRow?.selected_documents}
                        disableJobCost={disableJobCost}
                      />
                    </Grid>

                    {field.child &&
                      (() => {
                        const parentValue = String(values[field.name]);
                        const resolvedOptions =
                          field.child.options?.[parentValue];

                        if (!resolvedOptions) return null;

                        const childField = {
                          ...field.child,
                          options: resolvedOptions,
                        };

                        return (
                          <Grid
                            key={childField.name}
                            size={{
                              xs: 12,
                              md:
                                childField.name === "work_description" ? 8 : 4,
                            }}
                          >
                            <DelayedChildRender shouldRender={true}>
                              <RenderField
                                field={childField}
                                values={values}
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                              />
                            </DelayedChildRender>
                          </Grid>
                        );
                      })()}
                  </React.Fragment>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};

export default JaxFormFields;
